/**
 * HospiceDeathsBand: the company's current state, at the top of the
 * Availability board, in its own card above the people table. One compact band, two
 * blocks side by side (stacked on a phone): "On hospice (N)" and "Passed
 * away (N, last 8 weeks)". Each is a single wrapping row of small chips,
 * never a vertical list: the client's name, the date and the care
 * manager's initials, each chip a link to the client's record. Past
 * BAND_CHIPS_MAX chips (BAND_CHIPS_PHONE on a phone, where each chip
 * takes a line) a block shows "+N more", which opens a side panel listing
 * them all (name, date, care manager, a link to the record).
 *
 * Read from the client record (GET .../capacity/hospice-and-deaths),
 * never from the weekly call's mentions: a hospice day or a date of death
 * reaches this band only by being on the record. The order is the
 * server's: On hospice oldest first; Passed away newest first, then the
 * clients whose status says they died with no date recorded, when that
 * status was set in the same 8 weeks (LP-25), as "No date".
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Flower2, HandHeart } from 'lucide-react';
import { Drawer } from '@/components/primitives/Drawer';
import { TextAction } from '@/components/workspace/TextAction';
import { BAND_CHIPS_MAX, BAND_CHIPS_PHONE, MENTION_TAG_LOOK, bandDate, initialsOf } from '@/lib/callMentions';
import { proseDate } from '@/lib/proseDate';
import type { HospiceAndDeathsWire, RecordStatusClientWire } from '@/lib/capacityApi';

type BlockKind = 'hospice' | 'passed_away';

const GLYPH = { hospice: HandHeart, passed_away: Flower2 } as const;

function heading(kind: BlockKind, n: number): string {
  return kind === 'hospice' ? `On hospice (${n})` : `Passed away (${n}, last 8 weeks)`;
}

const EMPTY: Record<BlockKind, string> = {
  hospice: 'No one is on hospice.',
  passed_away: 'No one in the last 8 weeks.',
};

function BandChip({
  tenantSlug,
  kind,
  c,
  today,
  wideOnly,
}: {
  tenantSlug: string;
  kind: BlockKind;
  c: RecordStatusClientWire;
  today: string;
  /** Past the phone's count: shown from `sm` up only. */
  wideOnly: boolean;
}): ReactNode {
  const Glyph = GLYPH[kind];
  const date = c.date === null ? 'No date' : bandDate(c.date, today);
  const initials = initialsOf(c.cm_name);
  return (
    <Link
      to={`/t/${tenantSlug}/clients/${c.client_id}`}
      data-testid={`band-chip-${kind}`}
      title={`${c.display_name} · ${c.date === null ? 'no date recorded' : proseDate(c.date)}${c.cm_name ? ` · ${c.cm_name}` : ''}`}
      className={`${wideOnly ? 'hidden sm:inline-flex' : 'inline-flex'} max-w-full items-center gap-1 rounded-tight px-1.5 py-0.5 text-[0.6875rem] font-medium leading-4 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ${MENTION_TAG_LOOK[kind]}`}
    >
      <Glyph aria-hidden="true" className="h-3 w-3 shrink-0" strokeWidth={2.25} />
      <span className="truncate">{c.display_name}</span>
      <span className="shrink-0 opacity-80">{`· ${date}`}</span>
      {initials !== '' && <span className="shrink-0 opacity-80">{`· ${initials}`}</span>}
    </Link>
  );
}

function Block({
  tenantSlug,
  kind,
  clients,
  today,
  onMore,
}: {
  tenantSlug: string;
  kind: BlockKind;
  clients: RecordStatusClientWire[];
  today: string;
  onMore: () => void;
}): ReactNode {
  const shown = clients.slice(0, BAND_CHIPS_MAX);
  const rest = clients.length - shown.length;
  const restPhone = clients.length - Math.min(clients.length, BAND_CHIPS_PHONE);
  return (
    <section aria-label={heading(kind, clients.length)} data-testid={`band-${kind}`} className="min-w-0">
      <h3 className="text-label font-semibold uppercase tracking-[0.12em] text-sage-400">{heading(kind, clients.length)}</h3>
      {clients.length === 0 ? (
        <p className="mt-1 text-xs text-ink-soft">{EMPTY[kind]}</p>
      ) : (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {shown.map((c, i) => (
            <BandChip key={c.client_id} tenantSlug={tenantSlug} kind={kind} c={c} today={today} wideOnly={i >= BAND_CHIPS_PHONE} />
          ))}
          {rest > 0 && (
            <TextAction className="!py-0 hidden sm:inline-flex" data-testid={`band-more-${kind}`} onClick={onMore}>
              {`+${rest} more`}
            </TextAction>
          )}
          {restPhone > 0 && (
            <TextAction className="!py-0 sm:hidden" data-testid={`band-more-phone-${kind}`} onClick={onMore}>
              {`+${restPhone} more`}
            </TextAction>
          )}
        </div>
      )}
    </section>
  );
}

function PanelList({
  tenantSlug,
  kind,
  clients,
}: {
  tenantSlug: string;
  kind: BlockKind;
  clients: RecordStatusClientWire[];
}): ReactNode {
  return (
    <>
      <h2 className="mb-3 text-base font-semibold text-sage-800">{heading(kind, clients.length)}</h2>
      <ul className="divide-y divide-sage-800/[8%]" data-testid={`band-panel-list-${kind}`}>
        {clients.map((c) => (
          <li key={c.client_id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 text-sm">
            <Link
              to={`/t/${tenantSlug}/clients/${c.client_id}`}
              className="min-w-0 font-semibold text-sage-800 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
            >
              {c.display_name}
            </Link>
            <span className="text-ink">{c.date === null ? 'No date recorded' : proseDate(c.date)}</span>
            <span className="text-ink-soft">{c.cm_name ?? 'No care manager'}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function HospiceDeathsBand({
  tenantSlug,
  data,
  today,
}: {
  tenantSlug: string;
  data: HospiceAndDeathsWire;
  /** The practice day, 'YYYY-MM-DD'. */
  today: string;
}): ReactNode {
  const [panel, setPanel] = useState<BlockKind | null>(null);
  const listFor = (kind: BlockKind): RecordStatusClientWire[] => (kind === 'hospice' ? data.on_hospice : data.passed_away);

  return (
    <div data-testid="hospice-deaths-band" className="grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-2">
      <Block tenantSlug={tenantSlug} kind="hospice" clients={data.on_hospice} today={today} onMore={() => setPanel('hospice')} />
      <Block tenantSlug={tenantSlug} kind="passed_away" clients={data.passed_away} today={today} onMore={() => setPanel('passed_away')} />
      {panel !== null && (
        <Drawer onClose={() => setPanel(null)} ariaLabel={heading(panel, listFor(panel).length)} testId="band-panel">
          <PanelList tenantSlug={tenantSlug} kind={panel} clients={listFor(panel)} />
        </Drawer>
      )}
    </div>
  );
}
